/** Jobs-hub display helpers: status labels/tones, plain-English humanisers for
 *  raw codes (application types, decisions, statuses, flag keys), and the
 *  deadline chip derived from the server's `days_remaining` (NEVER re-derived —
 *  negative = overdue, positive = future, null = no deadline; brief §4). */

import type { JobStatus, FeeKind, FeeStatus, WorkstreamStatus, DrawingStatus } from "@/types";
import type { StatusTone } from "./status";

/* ─── Generic humaniser ────────────────────────────────────────────────── */

/** snake_case / kebab-case → Title Case. The default for any raw code the UI
 *  must never surface verbatim. "—" for empty. */
export function humanise(value: string | null | undefined): string {
  if (!value) return "—";
  return value
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/* ─── Job address ──────────────────────────────────────────────────────────
 * The single source of truth for "this job's address as a display string".
 * Reused by the job header, the application displays (which inherit the linked
 * job's address — applications no longer surface their own site_address except
 * as an orphan fallback), and the draft-application create form. */

/** The subset of a job's fields this helper reads. Every field is optional so it
 *  works with both the full job (structured fields) and the lean embedded jobs
 *  carried on list rows (which only have `full_address`). */
export interface JobAddressFields {
  job_name?: string | null;
  full_address?: string | null;
  address_line_1?: string | null;
  address_line_2?: string | null;
  address_line_3?: string | null;
  city?: string | null;
  county?: string | null;
}

// "__skip__" in address_line_1 is the sentinel for "deliberately not enriched"
// (the Ideal Postcodes lookup was skipped) — treat it as no structured address.
const ADDRESS_SKIP_SENTINEL = "__skip__";

/** Build a job's address as a comma-joined string. Prefers the structured
 *  fields (postcode excluded — it's shown separately), falls back to the legacy
 *  `full_address`, then to `job_name` as a last-resort label. Returns "" only
 *  when the job has no address-like field at all. */
export function buildJobAddressString(job: JobAddressFields): string {
  const line1 = (job.address_line_1 ?? "").trim();
  if (line1 && line1 !== ADDRESS_SKIP_SENTINEL) {
    return [job.address_line_1, job.address_line_2, job.address_line_3, job.city, job.county]
      .map((s) => (s ?? "").trim())
      .filter((s) => s !== "" && s !== ADDRESS_SKIP_SENTINEL)
      .join(", ");
  }
  return (job.full_address ?? "").trim() || (job.job_name ?? "").trim() || "";
}

/* ─── Job status ───────────────────────────────────────────────────────── */

const JOB_STATUS_META: Record<JobStatus, { label: string; tone: StatusTone }> = {
  not_started: { label: "Not started", tone: "info-soft" },
  in_progress: { label: "In progress", tone: "info" },
  submitted: { label: "Submitted", tone: "info" },
  with_client: { label: "With client", tone: "accent" },
  on_hold: { label: "On hold", tone: "neutral" },
  completed: { label: "Completed", tone: "success" },
  archived: { label: "Archived", tone: "neutral" },
};

export function jobStatusLabel(status: string): string {
  return JOB_STATUS_META[status as JobStatus]?.label ?? humanise(status);
}

export function jobStatusTone(status: string): StatusTone {
  return JOB_STATUS_META[status as JobStatus]?.tone ?? "neutral";
}

/** Combined `{ label, tone }` for the job-level overall-status pill — the
 *  derived `stage`/`status` (the 7-value set). Unknown/stray legacy values fall
 *  back to a neutral humanised pill. */
export function jobStageToPill(status: string): { label: string; tone: StatusTone } {
  return { label: jobStatusLabel(status), tone: jobStatusTone(status) };
}

/* ─── Drawing-sets model (Addendum 2) ──────────────────────────────────────
 * Display helpers for the job → drawing_set → drawing hierarchy. Tones reuse the
 * app's StatusTone vocab (no `--warning` in pills) — `with_client` maps to
 * `accent`, matching the legacy job-status `with_client` treatment. */

const WORKSTREAM_STATUS_META: Record<WorkstreamStatus, { label: string; tone: StatusTone }> = {
  not_applicable: { label: "—", tone: "neutral" },
  not_started: { label: "Not started", tone: "neutral" },
  drawings_in_progress: { label: "In progress", tone: "info" },
  with_client: { label: "With client", tone: "accent" },
  ready_to_submit: { label: "Ready to submit", tone: "info-soft" },
  submitted: { label: "Submitted", tone: "info" },
  approved: { label: "Approved", tone: "success" },
  needs_attention: { label: "Needs attention", tone: "danger" },
  complete: { label: "Complete", tone: "success" },
};

export function workstreamStatusLabel(status: string): string {
  return WORKSTREAM_STATUS_META[status as WorkstreamStatus]?.label ?? humanise(status);
}
export function workstreamStatusTone(status: string): StatusTone {
  return WORKSTREAM_STATUS_META[status as WorkstreamStatus]?.tone ?? "neutral";
}
/** Combined `{ label, tone }` for a rollup status pill (set header_status +
 *  per-type rollups). */
export function workstreamStatusToPill(status: string): { label: string; tone: StatusTone } {
  return { label: workstreamStatusLabel(status), tone: workstreamStatusTone(status) };
}

const DRAWING_STATUS_META: Record<DrawingStatus, { label: string; tone: StatusTone }> = {
  not_started: { label: "Not started", tone: "neutral" },
  in_progress: { label: "In progress", tone: "info" },
  with_client: { label: "With client", tone: "accent" },
  complete: { label: "Complete", tone: "success" },
};

export function drawingStatusLabel(status: string): string {
  return DRAWING_STATUS_META[status as DrawingStatus]?.label ?? humanise(status);
}
export function drawingStatusTone(status: string): StatusTone {
  return DRAWING_STATUS_META[status as DrawingStatus]?.tone ?? "neutral";
}

/** Pill props for a drawing's `display_status` — its raw lifecycle status until
 *  complete, then a workstream rollup. Prefer the drawing-level vocab (so a raw
 *  `in_progress` keeps its info tone; the rollup vocab only knows
 *  `drawings_in_progress`), falling back to the rollup vocab for
 *  ready_to_submit/submitted/approved/needs_attention. */
export function displayStatusToPill(status: string): { label: string; tone: StatusTone } {
  if (Object.prototype.hasOwnProperty.call(DRAWING_STATUS_META, status)) {
    return { label: drawingStatusLabel(status), tone: drawingStatusTone(status) };
  }
  return workstreamStatusToPill(status);
}

/** Plain-English drawing/workstream type label. */
export function workstreamTypeLabel(type: string): string {
  return type === "planning"
    ? "Planning"
    : type === "building_control"
      ? "Building control"
      : type === "other"
        ? "Other"
        : humanise(type);
}

/** `{job_number}-{set_number}` set code, e.g. 4101-1 (integers, no padding). */
export function setCode(jobNumber: number, setNumber: number): string {
  return `${jobNumber}-${setNumber}`;
}

/** `{job_number}-{set_number}-{drawing_number}` drawing code, e.g. 4101-1-2. */
export function drawingCode(jobNumber: number, setNumber: number, drawingNumber: number): string {
  return `${jobNumber}-${setNumber}-${drawingNumber}`;
}

/** A storage-only container set (portal document shares), not a production set.
 *  Containers are created non-current with no close stamp; real sets are either
 *  current or demoted with closed_at > 0 — so this pair is the discriminator.
 *  The status recompute never reads non-current sets, which is what keeps
 *  container contents out of the workstream rollups. */
export function isContainerSet(set: { is_current: boolean; closed_at: number }): boolean {
  return !set.is_current && set.closed_at === 0;
}

/** A job shows derived workstream pills once it has any drawing work (some
 *  rollup ≠ not_applicable); otherwise the cell/header falls back to the legacy
 *  `stage`/`status` pill. */
export function hasDerivedWorkstreamStatus(j: {
  planning_status?: WorkstreamStatus;
  bc_status?: WorkstreamStatus;
  other_status?: WorkstreamStatus;
  header_status?: WorkstreamStatus;
  is_complete?: boolean;
}): boolean {
  return Boolean(
    j.is_complete ||
      (j.planning_status && j.planning_status !== "not_applicable") ||
      (j.bc_status && j.bc_status !== "not_applicable") ||
      (j.other_status && j.other_status !== "not_applicable") ||
      (j.header_status && j.header_status !== "not_applicable"),
  );
}

/** Short kanban chip label for the auto-task types (manual cards aren't tagged). */
export function taskTypeLabel(type: string): string {
  return type === "draw" ? "Draw" : type === "submit" ? "Submit" : "Task";
}

/* ─── Tasks ────────────────────────────────────────────────────────────── */

// String-keyed so the cross-job Tasks dashboard's wider vocab (urgent, blocked)
// is covered too — additive over the per-job drawer's low/medium/high +
// open/in_progress/done/cancelled.
const TASK_PRIORITY_TONE: Record<string, StatusTone> = {
  low: "info-soft",
  medium: "info",
  high: "danger",
  urgent: "danger",
};
const TASK_STATUS_TONE: Record<string, StatusTone> = {
  open: "info",
  in_progress: "accent",
  blocked: "danger",
  done: "success",
  cancelled: "neutral",
};

export function taskPriorityTone(priority: string): StatusTone {
  return TASK_PRIORITY_TONE[priority] ?? "neutral";
}
export function taskStatusTone(status: string): StatusTone {
  return TASK_STATUS_TONE[status] ?? "neutral";
}

/* ─── Applications ─────────────────────────────────────────────────────── */

/** Application status tone — we don't know the full vocab, so colour by
 *  open/closed and render the humanised text. */
export function applicationStatusTone(isOpen: boolean): StatusTone {
  return isOpen ? "info" : "neutral";
}

/** Best-effort decision tone from free-text outcome keywords. */
export function decisionTone(outcome: string | null | undefined): StatusTone {
  if (!outcome) return "neutral";
  const o = outcome.toLowerCase();
  if (/(approv|grant|allow|permit|pass|consent)/.test(o)) return "success";
  if (/(refus|reject|dismiss|declin|withdrawn|fail|invalid)/.test(o)) return "danger";
  return "info";
}

/* ─── Fees (Brief 04) ──────────────────────────────────────────────────── */

const FEE_TYPE_TONE: Record<FeeKind, StatusTone> = {
  service: "info",
  disbursement: "neutral",
};
const FEE_STATUS_TONE: Record<FeeStatus, StatusTone> = {
  pending: "info-soft",
  partially_invoiced: "info",
  fully_invoiced: "success",
};

export function feeTypeTone(t: string): StatusTone {
  return FEE_TYPE_TONE[t as FeeKind] ?? "neutral";
}
export function feeStatusTone(s: string): StatusTone {
  return FEE_STATUS_TONE[s as FeeStatus] ?? "neutral";
}

/* ─── Invoices (Brief 05) ──────────────────────────────────────────────── */

const INVOICE_STATUS_TONE: Record<string, StatusTone> = {
  draft: "info-soft",
  submitted: "info",
  awaiting_payment: "info",
  paid: "success",
  voided: "neutral",
  deleted: "neutral",
};

export function invoiceStatusTone(s: string): StatusTone {
  return INVOICE_STATUS_TONE[s] ?? "neutral";
}

/** Voided/deleted invoices are de-emphasised in the list. */
export function isDeadInvoiceStatus(s: string): boolean {
  return s === "voided" || s === "deleted";
}

/* ─── Letters (Brief 06) ───────────────────────────────────────────────── */

const LETTER_CONFIDENCE_TONE: Record<string, StatusTone> = {
  high: "success",
  medium: "info",
  low: "neutral",
};
export function letterConfidenceTone(c: string | null | undefined): StatusTone {
  return c ? LETTER_CONFIDENCE_TONE[c] ?? "neutral" : "neutral";
}

export function letterSourceLabel(source: string): string {
  return source === "building_control" ? "Building control" : source === "planning" ? "Planning" : humanise(source);
}
export function letterSourceTone(source: string): StatusTone {
  return source === "building_control" ? "accent" : "info";
}

/* ─── Active flags ─────────────────────────────────────────────────────── */

/** Curated labels for the designation flags (nicer than the title-case
 *  fallback for acronyms). Any key not listed falls back to {@link humanise}. */
const FLAG_LABELS: Record<string, string> = {
  conservation_area: "Conservation area",
  listed_building: "Listed building",
  article_4_direction: "Article 4 direction",
  green_belt: "Green belt",
  flood_risk_zone: "Flood risk zone",
  tree_preservation_zone: "Tree preservation order",
  area_of_outstanding_natural_beauty: "AONB",
  scheduled_monument: "Scheduled monument",
  national_park: "National park",
  world_heritage_site: "World Heritage Site",
};

export function flagLabel(key: string): string {
  return FLAG_LABELS[key] ?? humanise(key);
}

/* ─── Deadline chip ────────────────────────────────────────────────────── */

export type DeadlineTone = "overdue" | "soon" | "future" | "none";

export interface DeadlineChip {
  label: string;
  tone: DeadlineTone;
}

function plural(n: number): string {
  return n === 1 ? "" : "s";
}

/** Deadline chip from the server's `days_remaining` (brief §3.2 rules). The
 *  server already snaps to UTC midnight — do not recompute from a timestamp. */
export function deadlineChip(daysRemaining: number | null | undefined): DeadlineChip {
  if (daysRemaining == null) return { label: "No deadline", tone: "none" };
  if (daysRemaining < 0) {
    const n = Math.abs(daysRemaining);
    return { label: `Overdue by ${n} day${plural(n)}`, tone: "overdue" };
  }
  if (daysRemaining === 0) return { label: "Due today", tone: "soon" };
  if (daysRemaining <= 7) return { label: `Due in ${daysRemaining} day${plural(daysRemaining)}`, tone: "soon" };
  return { label: `In ${daysRemaining} days`, tone: "future" };
}

/** Compact days-to-due chip for the dense Invoices dashboard table (Retool
 *  `dueDaysToPill` style: "{days}d" / "Today"), reusing the app's deadline tone
 *  palette. `days_to_due` follows the project convention (negative = overdue). */
export function invoiceDaysChip(days: number | null | undefined): DeadlineChip {
  if (days == null) return { label: "—", tone: "none" };
  if (days < 0) return { label: `${days}d`, tone: "overdue" };
  if (days === 0) return { label: "Today", tone: "soon" };
  if (days <= 7) return { label: `${days}d`, tone: "soon" };
  return { label: `${days}d`, tone: "future" };
}
